import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../../../../redux/slices/appContext'

const SeDebugContext = createContext(null)

const buildSeCode = (endpoint, suffix) => `se:${endpoint}:${suffix}`

export function SeDebugProvider ({
  config,
  children,
  defaultEndpoint = 'sf-status-report',
  defaultPolicy = 'wealthOwners',
  defaultTag = 'live'
}) {
  const { isSummitUser } = useAppContext()

  const endpoint = config?.endpoint || defaultEndpoint
  const policy = config?.policy || defaultPolicy
  const tag = config?.tag || defaultTag
  const devPolicy = config?.devPolicy || policy
  const devTag = config?.devTag || tag
  const showDebugControl = config?.showDebugControl !== false

  const prodSuffix = `${policy}:${tag}`
  const devSuffix = `${devPolicy}:${devTag}`
  const [suffix, setSuffix] = useState(isSummitUser ? devSuffix : prodSuffix)

  useEffect(() => {
    setSuffix(isSummitUser ? devSuffix : prodSuffix)
  }, [prodSuffix, devSuffix, isSummitUser])

  const value = useMemo(() => ({
    suffix,
    setSuffix,
    prodSuffix,
    devSuffix,
    showDebugControl,
    codes: { list: buildSeCode(endpoint, suffix) }
  }), [suffix, prodSuffix, devSuffix, showDebugControl, endpoint])

  return (
    <SeDebugContext.Provider value={value}>
      {children}
    </SeDebugContext.Provider>
  )
}

SeDebugProvider.propTypes = {
  config: PropTypes.object,
  children: PropTypes.node.isRequired,
  defaultEndpoint: PropTypes.string,
  defaultPolicy: PropTypes.string,
  defaultTag: PropTypes.string
}

export function useSeCodes ({
  defaultEndpoint = 'sf-status-report',
  defaultPolicy = 'wealthOwners',
  defaultTag = 'live'
} = {}) {
  const ctx = useContext(SeDebugContext)
  if (!ctx) {
    return { list: buildSeCode(defaultEndpoint, `${defaultPolicy}:${defaultTag}`) }
  }
  return ctx.codes
}

export function useSeDebug () {
  return useContext(SeDebugContext)
}
